import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";

const LANG: Record<string, Highcharts.LangOptions> = {
  "pt-BR": {
    months: [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    shortMonths: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
    weekdays: ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"],
    downloadPNG: "Baixar imagem PNG",
    downloadJPEG: "Baixar imagem JPEG",
    downloadPDF: "Baixar em PDF",
    downloadSVG: "Baixar imagem SVG",
    printChart: "Imprimir gráfico",
    viewFullscreen: "Ver em tela cheia",
    exitFullscreen: "Sair da tela cheia",
    contextButtonTitle: "Menu do gráfico",
  },
};

if (typeof window !== "undefined") {
  HighchartsExporting(Highcharts);
  HighchartsOfflineExporting(Highcharts);
  const locale = document.documentElement.lang;
  Highcharts.setOptions({ lang: LANG[locale] ?? {} });
}

export type RefObjectForHighchartsReact = {
  chart: Highcharts.Chart;
  container: React.RefObject<HTMLDivElement>;
};

interface HighchartsChartProps {
  options: Highcharts.Options;
  constructorType?: string;
  className?: string;
  immutable?: boolean;
  allowChartUpdate?: boolean;
  forwardedRef?: React.ForwardedRef<RefObjectForHighchartsReact | null>;
}
const HighchartsChart = React.forwardRef<RefObjectForHighchartsReact | null, HighchartsChartProps>(
  (
    { options, constructorType = "stockChart", className, immutable = false, allowChartUpdate = true, forwardedRef },
    chartRef,
  ) => {
    const intl = useIntl();
    const theme = useTheme();
    const dark = theme === "dark";

    useEffect(() => {
      Highcharts.setOptions({
        lang: LANG[intl.locale] ?? {},
      });
    }, [intl.locale]);

    return (
      <div className={classNames("min-w-full touch-none rounded-xl", className)}>
        <HighchartsReact
          ref={forwardedRef || chartRef}
          highcharts={Highcharts}
          constructorType={constructorType}
          immutable={immutable}
          allowChartUpdate={allowChartUpdate}
          options={{
            ...options,
            exporting: {
              enabled: true,
              chartOptions: {
                chart: {
                  backgroundColor: dark ? colors.slate[900] : "#ffffff",
                  style: { fontFamily: aeonik.style.fontFamily },
                  spacing: [32, 32, 32, 32],
                },
                navigator: { enabled: false },
                scrollbar: { enabled: false },
                rangeSelector: { enabled: false },
                credits: {
                  enabled: true,
                  text: "Fundamentei.com",
                  href: "https://fundamentei.com",
                  position: {
                    align: "center",
                  },
                  style: {
                    fontSize: "10px",
                    fontWeight: "bold",
                  },
                } satisfies Highcharts.CreditsOptions,
              },
              buttons: {
                contextButton: {
                  menuItems: ["downloadPNG", "downloadJPEG", "downloadPDF"],
                  symbolStroke: dark ? colors.slate[300] : colors.slate[600],
                  theme: {
                    fill: "transparent",
                    states: {
                      hover: { fill: dark ? colors.slate[700] : colors.slate[100] },
                      select: { fill: dark ? colors.slate[700] : colors.slate[100] },
                    },
                  },
                },
              },
            },
            accessibility: {
              enabled: false,
            },
          }}
          containerProps={{
            style: {
              width: "100%",
              height: "100%",
            },
          }}
        />
      </div>
    );
  },
);

export default HighchartsChart;
